/**
 * <EnrollmentPanel /> — Settings → Enrollment tab.
 *
 * - Mint single-use enrollment codes (dialog with platform/label/TTL).
 * - List existing enrollments with status badges that update via 10s poll.
 * - Click a row to open a drawer with the device info collected on first
 *   check-in.
 * - Revoke pending enrollments inline.
 *
 * Backend spec: docs/backend-requirements/edr-agent-enrollment-api.md
 */
import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { InstallersPanel } from "./installers/InstallersPanel";
import { Panel } from "@/components/surface/Panel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DetailDrawer } from "@/components/ui/detail-drawer";
import { toast } from "sonner";
import { Copy, Check, RefreshCw, Plus, RotateCw } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import {
  useEdrEnrollments,
  useEdrEnrollmentDetail,
  useMintEnrollment,
  useRevokeEnrollment,
  useRefreshEnrollment,
  type EnrollmentPlatform,
  type EnrollmentStatus,
  type MintEnrollmentResult,
  type EnrollmentSummary,
} from "@/hooks/security/useEdrEnrollment";
import { cn } from "@/lib/utils";

const STATUS_STYLES: Record<EnrollmentStatus, string> = {
  pending: "bg-warning-muted text-warning-muted-foreground border-warning-border",
  enrolled: "bg-success-muted text-success-muted-foreground border-success-border",
  expired: "bg-muted text-muted-foreground border-border",
  revoked: "bg-muted text-muted-foreground border-border",
  failed: "bg-destructive/10 text-destructive border-destructive/30",
};

function StatusBadge({ status }: { status: EnrollmentStatus }) {
  return (
    <span
      className={cn(
        "px-1.5 py-0.5 rounded text-[10px] border uppercase tracking-wider font-semibold",
        STATUS_STYLES[status],
      )}
    >
      {status}
    </span>
  );
}

export function EnrollmentPanel() {
  return (
    <Tabs defaultValue="installer" className="w-full space-y-4">
      <TabsList className="h-9">
        <TabsTrigger value="installer" className="text-xs">Pre-baked installer</TabsTrigger>
        <TabsTrigger value="code" className="text-xs">Advanced: single-use code</TabsTrigger>
      </TabsList>
      <TabsContent value="installer" className="mt-0">
        <InstallersPanel />
      </TabsContent>
      <TabsContent value="code" className="mt-0">
        <EnrollmentCodesPanel />
      </TabsContent>
    </Tabs>
  );
}

function EnrollmentCodesPanel() {
  const listQ = useEdrEnrollments();
  const [mintOpen, setMintOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mintedResult, setMintedResult] = useState<MintEnrollmentResult | null>(null);

  const result = listQ.data;
  const unavailable = result?.kind === "unavailable";
  const rows = result?.kind === "ok" ? result.rows : [];

  return (
    <>
      <Panel
        title="Agent enrollment"
        subtitle={
          unavailable
            ? "Backend endpoint not provisioned yet"
            : `${rows.length} enrollment${rows.length === 1 ? "" : "s"}`
        }
        actions={
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => listQ.refetch()}
              disabled={listQ.isFetching}
            >
              <RefreshCw className={cn("h-3.5 w-3.5", listQ.isFetching && "animate-spin")} />
            </Button>
            <Button size="sm" onClick={() => setMintOpen(true)} disabled={unavailable}>
              <Plus className="h-3.5 w-3.5 mr-1" /> Mint code
            </Button>
          </div>
        }
        loading={listQ.isLoading}
        isError={listQ.isError}
        error={listQ.error}
        onRetry={() => listQ.refetch()}
        empty={!listQ.isLoading && !listQ.isError && (unavailable || rows.length === 0)}
        emptyTitle={unavailable ? "Enrollment API not yet available" : "No enrollments yet"}
        emptyDescription={
          unavailable
            ? "Once the EDR enrollment endpoints ship, mint codes and view collected device info here."
            : "Mint a single-use code and run the install command on the target host to bring an agent online."
        }
        skeleton="table"
      >
        <div className="overflow-x-auto -mx-4 -my-4">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                {["Label", "Platform", "Status", "Device", "Created", "Expires", ""].map((h) => (
                  <th
                    key={h}
                    className="px-3 py-2 text-left font-semibold uppercase tracking-wider text-[10px] text-muted-foreground"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <EnrollmentRow
                  key={r.id}
                  row={r}
                  onSelect={() => setSelectedId(r.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <MintDialog
        open={mintOpen}
        onOpenChange={(o) => {
          setMintOpen(o);
          if (!o) setMintedResult(null);
        }}
        minted={mintedResult}
        onMinted={setMintedResult}
      />

      <EnrollmentDetailDrawer
        id={selectedId}
        onOpenChange={(o) => !o && setSelectedId(null)}
      />
    </>
  );
}

function EnrollmentRow({
  row,
  onSelect,
}: {
  row: EnrollmentSummary;
  onSelect: () => void;
}) {
  const revoke = useRevokeEnrollment();
  const refresh = useRefreshEnrollment();
  return (
    <tr
      className="border-b border-border/60 hover:bg-muted/40 cursor-pointer"
      onClick={onSelect}
    >
      <td className="px-3 py-2 text-foreground">{row.label || "—"}</td>
      <td className="px-3 py-2 text-muted-foreground">{row.platform ?? "—"}</td>
      <td className="px-3 py-2">
        <StatusBadge status={row.status} />
      </td>
      <td className="px-3 py-2 font-mono text-muted-foreground">
        {row.deviceName || (row.deviceId ? row.deviceId.slice(0, 12) : "—")}
      </td>
      <td className="px-3 py-2 text-muted-foreground tabular-nums">
        {formatDistanceToNow(new Date(row.createdAt), { addSuffix: true })}
      </td>
      <td className="px-3 py-2 text-muted-foreground tabular-nums">
        {row.expiresAt
          ? formatDistanceToNow(new Date(row.expiresAt), { addSuffix: true })
          : "—"}
      </td>
      <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-end gap-1">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0"
            disabled={refresh.isPending}
            onClick={() => refresh.mutate(row.id)}
            title="Refresh status"
          >
            <RotateCw className={cn("h-3.5 w-3.5", refresh.isPending && "animate-spin")} />
          </Button>
          {row.status === "pending" ? (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs"
              disabled={revoke.isPending}
              onClick={() => revoke.mutate(row.id)}
            >
              Revoke
            </Button>
          ) : null}
        </div>
      </td>
    </tr>
  );
}

function MintDialog({
  open,
  onOpenChange,
  minted,
  onMinted,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  minted: MintEnrollmentResult | null;
  onMinted: (m: MintEnrollmentResult) => void;
}) {
  const [label, setLabel] = useState("");
  const [platform, setPlatform] = useState<EnrollmentPlatform>("macos");
  const [ttl, setTtl] = useState("24");
  const mint = useMintEnrollment();

  const handleMint = async () => {
    try {
      const res = await mint.mutateAsync({
        label,
        platform,
        expiresInHours: Number(ttl) || 24,
      });
      onMinted(res);
    } catch {
      // toast handled by hook
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mint enrollment code</DialogTitle>
          <DialogDescription>
            Single-use code valid for the chosen window. Run the install command on
            the target host to bring an agent online.
          </DialogDescription>
        </DialogHeader>

        {minted ? (
          <MintedView minted={minted} />
        ) : (
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="label" className="text-xs">
                Label (optional)
              </Label>
              <Input
                id="label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. Marketing laptops"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Platform</Label>
                <Select
                  value={platform}
                  onValueChange={(v) => setPlatform(v as EnrollmentPlatform)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="macos">macOS</SelectItem>
                    <SelectItem value="windows">Windows</SelectItem>
                    <SelectItem value="linux">Linux</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ttl" className="text-xs">
                  Expires in (hours)
                </Label>
                <Input
                  id="ttl"
                  type="number"
                  min={1}
                  max={168}
                  value={ttl}
                  onChange={(e) => setTtl(e.target.value)}
                />
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          {minted ? (
            <Button onClick={() => onOpenChange(false)}>Done</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleMint} disabled={mint.isPending}>
                {mint.isPending ? "Minting…" : "Mint code"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      size="sm"
      variant="ghost"
      className="h-7 px-2"
      onClick={() => {
        navigator.clipboard.writeText(value).then(() => {
          setCopied(true);
          toast.success("Copied");
          setTimeout(() => setCopied(false), 1500);
        });
      }}
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
    </Button>
  );
}

function MintedView({ minted }: { minted: MintEnrollmentResult }) {
  return (
    <div className="space-y-3 py-2">
      <div>
        <Label className="text-xs text-muted-foreground">Enrollment code</Label>
        <div className="mt-1 flex items-center gap-2 rounded border border-border bg-muted/40 px-3 py-2">
          <code className="flex-1 font-mono text-sm">{minted.code}</code>
          <CopyButton value={minted.code} />
        </div>
        <p className="mt-1 text-[10px] text-muted-foreground">
          Shown once — copy it now. You can revoke and mint a new one if lost.
        </p>
      </div>
      {minted.installCommand ? (
        <div>
          <Label className="text-xs text-muted-foreground">Install command</Label>
          <div className="mt-1 flex items-center gap-2 rounded border border-border bg-muted/40 px-3 py-2">
            <code className="flex-1 font-mono text-xs break-all">
              {minted.installCommand}
            </code>
            <CopyButton value={minted.installCommand} />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function EnrollmentDetailDrawer({
  id,
  onOpenChange,
}: {
  id: string | null;
  onOpenChange: (o: boolean) => void;
}) {
  const q = useEdrEnrollmentDetail(id);
  const detail = q.data;

  return (
    <DetailDrawer
      open={!!id}
      onOpenChange={onOpenChange}
      title={detail?.label || (detail ? `Enrollment ${detail.id.slice(0, 12)}` : "Enrollment")}
      description={detail ? <StatusBadge status={detail.status} /> : undefined}
    >
      {q.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !detail ? (
        <p className="text-sm text-muted-foreground">
          Detail endpoint not yet available.
        </p>
      ) : (
        <div className="space-y-5">
          <Section label="Enrollment">
            <Row k="ID" v={<code className="font-mono text-xs">{detail.id}</code>} />
            <Row k="Platform" v={detail.platform} />
            <Row
              k="Created"
              v={formatDistanceToNow(new Date(detail.createdAt), { addSuffix: true })}
            />
            <Row
              k="Expires"
              v={
                detail.expiresAt
                  ? formatDistanceToNow(new Date(detail.expiresAt), { addSuffix: true })
                  : "—"
              }
            />
            <Row
              k="Enrolled"
              v={
                detail.enrolledAt
                  ? formatDistanceToNow(new Date(detail.enrolledAt), { addSuffix: true })
                  : "—"
              }
            />
          </Section>

          <Section label="Collected device info">
            {detail.device ? (
              <>
                <Row k="Hostname" v={detail.device.hostname} />
                <Row k="Platform" v={detail.device.platform} />
                <Row k="OS version" v={detail.device.osVersion} />
                <Row k="Architecture" v={detail.device.arch} />
                <Row k="Agent version" v={detail.device.agentVersion} />
                <Row k="Serial number" v={detail.device.serialNumber} />
                <Row k="Public IP" v={detail.device.publicIp} />
                <Row k="Private IP" v={detail.device.privateIp} />
                <Row k="Console user" v={detail.device.consoleUser} />
                <Row
                  k="First seen"
                  v={
                    detail.device.firstSeenAt
                      ? formatDistanceToNow(new Date(detail.device.firstSeenAt), {
                          addSuffix: true,
                        })
                      : undefined
                  }
                />
                <Row
                  k="Last seen"
                  v={
                    detail.device.lastSeenAt
                      ? formatDistanceToNow(new Date(detail.device.lastSeenAt), {
                          addSuffix: true,
                        })
                      : undefined
                  }
                />
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                Waiting for the agent's first check-in. This view will populate
                automatically once the host enrolls.
              </p>
            )}
          </Section>
        </div>
      )}
    </DetailDrawer>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
        {label}
      </h3>
      <div className="rounded border border-border divide-y divide-border">{children}</div>
    </div>
  );
}

function Row({ k, v }: { k: string; v?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 px-3 py-2 text-xs">
      <span className="text-muted-foreground">{k}</span>
      <span className="text-foreground text-right break-all">{v || "—"}</span>
    </div>
  );
}
